/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, {
  memo,
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button, ButtonBase } from "@mui/material";

import { Pagination, Autoplay, FreeMode } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { PrivacyScreen } from "@capacitor-community/privacy-screen";
import { Transposer } from "chord-transposer";
import Skeleton from "react-loading-skeleton";
import { message, Modal, Slider, Spin } from "antd";
import { Share } from "@capacitor/share";
import QuickPinchZoom, { make3dTransformValue } from "react-quick-pinch-zoom";
import { KeepAwake } from "@capacitor-community/keep-awake";
import * as htmlToImage from "html-to-image";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";
import { AndroidSettings, NativeSettings } from "capacitor-native-settings";
import { useSwipeable } from "react-swipeable";
import { motion, useMotionValue, useTransform } from "framer-motion";

// import { ReactComponent as ArrowLeft } from "../../assets/icons/arrow-back.svg";
// import { ReactComponent as PauseIcon } from "../../assets/icons/pause.svg";
// import { ReactComponent as PlayIcon } from "../../assets/icons/play.svg";
import { ReactComponent as Tag } from "../../assets/icons/tag2.svg";
import { ReactComponent as SettingOn } from "../../assets/icons/setting-on.svg";
// import { ReactComponent as SettingOff } from "../../assets/icons/setting-off.svg";
import { ReactComponent as Copy } from "../../assets/icons/copy.svg";
import { ReactComponent as Edit } from "../../assets/icons/edit.svg";
import { ReactComponent as LightMode } from "../../assets/icons/light-mode.svg";
import { ReactComponent as DarkMode } from "../../assets/icons/dark-mode.svg";
import { ReactComponent as ShareIcon } from "../../assets/icons/share.svg";
import { ReactComponent as CameraOn } from "../../assets/icons/camera-on.svg";
import { ReactComponent as CameraOff } from "../../assets/icons/camera-off.svg";
import { ReactComponent as ViewIcon } from "../../assets/icons/view-arrow.svg";
// import { ReactComponent as PlaylistOnIcon } from "../../assets/icons/plus-circle-on.svg";
// import { ReactComponent as PlaylistOffIcon } from "../../assets/icons/plus-circle-off.svg";
import { ReactComponent as ReportOnIcon } from "../../assets/icons/report-on.svg";
import { ReactComponent as ReportOffIcon } from "../../assets/icons/report-off.svg";
import { ReactComponent as BackArrowIcon } from "../../assets/icons/arrow-back.svg";
import { ReactComponent as HalfStar } from "../../assets/icons/half-star.svg";
import { ReactComponent as FullStar } from "../../assets/icons/fill-star.svg";
import { ReactComponent as EmptyStar } from "../../assets/icons/empty-star.svg";
import { ReactComponent as LyricsImageOnIcon } from "../../assets/icons/lyrics-image-on.svg";
import { ReactComponent as LyricsImageOffIcon } from "../../assets/icons/lyrics-image-off.svg";
import { ReactComponent as BookMapOn } from "../../assets/icons/BookmarkUpdate.svg";
import { ReactComponent as FontMinus } from "../../assets/icons/FontMinus.svg";
import { ReactComponent as FontPlus } from "../../assets/icons/FontPlus.svg";
import { ReactComponent as FontRefresh } from "../../assets/icons/FontRefresh.svg";
// import { ReactComponent as StopIcon } from "../../assets/icons/Stop.svg";

// import { ReactComponent as MoreOptions } from "../../assets/icons/moreOptionIcon.svg";
import {
  awake,
  customSplit,
  formatNumber,
  getChordImages,
  getDecoded,
  getPlatform,
  getSlowestScrollStep,
  isEnglishWord,
  removeChordFromLyrics,
  showPlayStoreModal,
  sleep,
} from "../../utils";
import guitarQr from "../../assets/images/guitar-qr.jpg";
import logo from "../../assets/images/logo-horizontal.png";

import BackHeader from "./BackHeader";
import { useStore } from "../../store";
import CustomButton from "./CustomButton";
import CustomDrawer from "./CustomDrawer";
import CustomWheelPicker from "./CustomWheelPicker";
import { useLocation, useNavigate } from "react-router-dom";
import { SongDetailsType, SongType } from "../../types";
import api from "../../api/apiClient";
import { endpoints } from "../../api/endpoints";
import { useRouteTracker } from "./RouteTracker";
import VideoCapture from "./VideoCapture";
import config from "../../config";
import { AdService } from "../../ads/ad";
import CustomPhotoUpload from "./CustomPhotoUpload";
import CustomImage from "./CustomImage";
import CustomPhotoUploadType2 from "./CustomPhotoUploadType2";
import Loader from "./Loader";
import Footer from "./Footer";
import FloatingScrollUi from "./FloatingScrollUi";
import { PlusMinusButton } from "./PlusMinusButton";

const ads = new AdService();

const SongDetail: React.FC = () => {
  let [lyrics, setLyrics] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  let [chordPositions, setChordPositions] = useState<Record<string, string>>(
    {}
  );
  const [songDetailData, setSongDetailData] = useState<SongDetailsType>();
  const [scroll, setScroll] = useState<string>("0x");
  const [isSettingDrawerOpen, setIsSettingDrawerOpen] = useState(false);
  const [lyricsFontsize, setLyricsFontsize] = useState("16px");
  const [chordFontsize, setChordFontsize] = useState("16px");
  const [orgChordPositions, setOrgChordPositions] = useState<
    Record<string, string>
  >({});
  const o = localStorage.getItem("lightThemeOn") === "true" ? true : false;
  const [lightThemeOn, setLightThemeOn] = useState(o);
  const [lyricsImageOn, setLyricsImageOn] = useState(false);
  const [userRating, setUserRating] = useState<number | null>(null);
  const [lyricsImgFullScreen, setLyricsImgFullScreen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipedColor, setSwipedColor] = useState("gray");

  const [clickedChord, setClickedChord] = useState("");
  const [isChordImageDrawerOpen, setIsChordImageDrawerOpen] = useState(false);

  const [leftSong, setLeftSong] = useState<null | {
    songId: number;
    chordId: number;
    name: string;
  }>();
  const [rightSong, setRightSong] = useState<null | {
    songId: number;
    chordId: number;
    name: string;
  }>();

  const swipedX = useMotionValue(0);

  const { showPlaylist } = useStore();

  const [direction, setDirection] = useState<"left" | "right" | null>(null);

  const shareBase64Obj = useRef<Record<string, string>>({});
  const currentTransposeLevel = useRef<undefined | number>();
  const currentFontSize = useRef<undefined | number>();

  const shareImgRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const song: SongType = location.state;

  const formattedLyrics = useRef("");

  const lyricsImgRef = useRef<any>();
  const onZoomUpdate = useCallback(({ x, y, scale }: any) => {
    const { current: img }: any = lyricsImgRef;
    if (img) {
      const value = make3dTransformValue({ x, y, scale });
      img.style.setProperty("transform", value);
    }
  }, []);

  const getDetail = async (chordId = song.chord_id, songId = song.song_id) => {
    const result: any = await api.get(
      endpoints.song.getDetail + `?songId=${songId}&chordId=${chordId}`
    );
    setLoading(false);
    if (result.code !== "200") return message.error(result.message);
    const songDetail: SongDetailsType = result.data;
    const onlyLyricsImgAvailable =
      songDetail.details.chord_img && !songDetail.details.chord_pro;
    const bothImgAndTextExist =
      songDetail?.details?.chord_pro && songDetail?.details?.chord_img;
    if (onlyLyricsImgAvailable || bothImgAndTextExist) {
      setLyricsImageOn(true);
    } else {
      setLyricsImageOn(false);
    }
    setSongDetailData(songDetail);
    const l = songDetail?.details?.chord_pro || "";
    formattedLyrics.current = l;
    const { positions, removedChordLyrics } = removeChordFromLyrics(l);
    setLyrics(removedChordLyrics);
    setUserRating(songDetail.rating.currentUserRating);
    setChordPositions(positions);
    setOrgChordPositions(positions);
    getAdditionalData(chordId, songId);
  };

  const getAdditionalData = async (
    chordId = song.chord_id,
    songId = song.song_id
  ) => {
    if (!song.dataApi || !song.data || song.data?.by?.length === 0) return;
    let arr: { songId: number; chordId: number; name: string }[] =
      song.data?.by?.map((s: SongType) => {
        return {
          songId: s.song_id,
          chordId: s.chord_id,
          name: s.name,
        };
      });

    const a: any = await api.get(song.dataApi);
    if (a.code !== "200") a.data = { by: [] };
    if (!song.selfCall) {
      // it means user swipe from song detail
      let b = a.data?.by?.map((s: SongType) => {
        return {
          songId: s.song_id,
          chordId: s.chord_id,
          name: s.name,
        };
      });
      arr = [...arr, ...b];
    }
    const currentSongIndex = arr.findIndex(
      (i) => chordId === i.chordId && songId === i.songId
    );

    setLeftSong(arr[currentSongIndex - 1]);
    setRightSong(arr[currentSongIndex + 1]);
  };

  const isUserScrolling = useRef(false);

  const handleTouchStart = () => (isUserScrolling.current = true);

  const handleTouchEnd = () => (isUserScrolling.current = false);

  const { store, updateStoreFunc } = useStore();
  const text = store.text;

  const headerHeight = store.cameraPreviewOn ? 0 : 110;

  const videoHeight = store.cameraPreviewOn ? config.videoHeight : 0;

  const handleClickEdit = () => {
    localStorage.setItem("chordPro", formattedLyrics.current);
    // localStorage.setItem("lyrics",lyrics.join('\n'));
    const songInfo = {
      singerName: songDetailData?.details.artist.name,
      songName: songDetailData?.details.name,
      singerId: songDetailData?.details.artist.artist_id.toString().split(","),
      chordId: songDetailData?.details.chord_id,
      songId: songDetailData?.details.id,
      songUid: songDetailData?.details.user_id,
      chordUid: songDetailData?.details.chord_uid,
      chordPro: songDetailData?.details?.chord_pro,
    };
    navigate("/dataEntry/addLyrics", { state: { songInfo: songInfo } });
  };

  const handleClickSetting = () => {
    setIsSettingDrawerOpen(true);
  };

  const handleClickScroll = (value: number) => {
    setScroll(`${value}x`);
    // setScroll((prevScroll) => {
    // const currentScrollValue = parseInt(prevScroll.replace('x', ''));
    // const nextScrollValue = currentScrollValue < 5 ? currentScrollValue + 1 : 0;
    // return `${value}x`;
    // });
  };

  const handleTransposerChange = (level: string) => {
    let l = Number(level);
    currentTransposeLevel.current = l;
    // Gather all chords into a single string
    const allChords = Object.values(orgChordPositions).join(" ");

    // Transpose the gathered chords
    const transposedChordsString =
      Number(l) > 0
        ? Transposer.transpose(allChords).up(Math.abs(l)).toString()
        : Transposer.transpose(allChords).down(Math.abs(l)).toString();

    if (!transposedChordsString) return;

    // Split the transposed string back into individual chords
    const transposedChordsArray = transposedChordsString.split(" ");

    // Create a new object with the transposed chords
    const transposedChords: Record<string, string> = {};
    Object.keys(chordPositions).forEach((position, index) => {
      transposedChords[position] = transposedChordsArray[index];
    });

    setChordPositions(transposedChords);
  };

  const handleChordLevelChange = (t: { id: string; value: string }) => {
    if (t.value === "0") {
      setChordPositions(orgChordPositions);
    } else {
      handleTransposerChange(t.value);
    }
  };

  const handleFontSizeChange = (value: { id: string; value: string }) => {
    currentFontSize.current = Number(value.value);
    switch (value.value) {
      case "-4":
        setLyricsFontsize("14px");
        setChordFontsize("14px");
        break;
      case "-3":
        setLyricsFontsize("14.5px");
        setChordFontsize("14.5px");
        break;
      case "-2":
        setLyricsFontsize("15px");
        setChordFontsize("15px");
        break;
      case "-1":
        setLyricsFontsize("15.5px");
        setChordFontsize("15.5px");
        break;
      case "0":
        setLyricsFontsize("16px");
        setChordFontsize("16px");
        break;
      case "1":
        setLyricsFontsize("16.5px");
        setChordFontsize("16.5px");
        break;
      case "2":
        setLyricsFontsize("17px");
        setChordFontsize("17px");
        break;
      case "3":
        setLyricsFontsize("17.5px");
        setChordFontsize("17.5px");
        break;
      case "4":
        setLyricsFontsize("18px");
        setChordFontsize("18px");
        break;
    }
  };

  const handleThemeChange = () => {
    setLightThemeOn((pre: any) => {
      localStorage.setItem("lightThemeOn", JSON.stringify(!pre));
      return !pre;
    });
  };

  const handleLyricsImageChange = () => {
    setLyricsImageOn((pre) => !pre);
  };

  const decoded = getDecoded();
  const uid = decoded?.userId;
  const isUserOwnChord =
    uid === songDetailData?.details.chord_uid ? true : false;

  const backgroundColor = lightThemeOn ? "#E9E9E9" : config.color.htmlColor;
  const headerColor = lightThemeOn ? "black" : "white";
  const lyricsColor = lightThemeOn ? "#111111" : "#FFFFFF";
  const chordColor = lightThemeOn ? "#2E5CFF" : "#FFB800";

  const bodyHeaderStyle = () => {
    let style = {} as React.CSSProperties;

    if (store.cameraPreviewOn) {
      style = {
        ...style,
        paddingTop: 0,
        backgroundColor: "transparent",
        position: "fixed",
        top: videoHeight - 55,
        left: "50%",
        transform: "translate(-50%)",
        textAlign: "center",
      } as React.CSSProperties;
    } else {
      style = {
        ...style,
        paddingTop: downloading ? "10px" : "60px",
        backgroundColor: backgroundColor,
      } as React.CSSProperties;
    }
    return style;
  };

  const handleRating = async (rating: number) => {
    setUserRating(rating);
    Modal.confirm({
      title: "Rate",
      content: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            margin: "30px 0",
          }}
        >
          {[1, 2, 3, 4, 5].map((star) => (
            <span key={star}>
              {(rating as number) >= star
                ? getStar("full", "2rem")
                : getStar("empty", "2rem")}
            </span>
          ))}
        </div>
      ),
      okText: text["Yes"],
      cancelText: text["No"],
      centered: true,
      async onOk() {
        const result: any = await api.post(endpoints.song.rate, {
          star: rating,
          songId: songDetailData?.details.id,
          chordId: songDetailData?.details.chord_id,
        });
        if (result.code === "200") {
          message.success(result.message);
          getDetail();
        } else {
          message.error(result.message);
        }
      },
      onCancel() {
        setUserRating(songDetailData?.rating.currentUserRating || null);
      },
    });
  };

  const getAvgStars = (averageRating: number) => {
    const stars = [];

    for (let i = 1; i <= 5; i++) {
      if (i <= Math.floor(averageRating)) {
        stars.push(getStar("full"));
      } else if (i === Math.ceil(averageRating) && averageRating % 1 >= 0.5) {
        stars.push(getStar("half"));
      } else {
        stars.push(getStar("empty"));
      }
    }

    return stars;
  };

  let starKey = 0;

  const getStar = (type: string, size: string = "1rem") => {
    const starStyle = {
      width: size,
      height: size,
      marginRight: "5px",
    } as React.CSSProperties;

    starKey++; // just for solve(each child in a list should have a unique "key" prop.)

    if (type === "full") {
      return <FullStar key={starKey} style={starStyle} />;
    } else if (type === "half") {
      return <HalfStar key={starKey} style={starStyle} />;
    } else {
      return <EmptyStar key={starKey} style={starStyle} />;
    }
  };

  const renderRating = () => {
    return (
      <>
        {!store.cameraPreviewOn && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "10px 0",
              margin: "40px 10px 30px 10px",
              borderWidth: "1px 0 1px 0",
              borderStyle: "solid",
              borderColor: "#242424",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "normal",
                  color: lightThemeOn ? "black" : "white",
                }}
              >
                {songDetailData?.rating.totalUsers} ratings
              </div>
              <div
                style={{
                  fontSize: "60px",
                  fontWeight: "bold",
                  color: lightThemeOn ? "black" : "white",
                }}
              >
                {formatNumber(songDetailData?.rating.avg || 0)}
              </div>
              <div style={{}}>
                {getAvgStars(songDetailData?.rating.avg || 0)}
              </div>
            </div>
            <div
              style={{
                borderStyle: "solid",
                borderColor: "#242424",
                borderWidth: "0 0 0 1px",
                paddingLeft: "20px",
              }}
            >
              <div
                style={{
                  fontSize: "1rem",
                  fontWeight: "normal",
                  marginBottom: "15px",
                  color: "grey",
                  textAlign: "center",
                  fontStyle: userRating ? "italic" : "normal",
                }}
              >
                {userRating ? "Rated" : "Tap to rate"}
              </div>
              <div>
                {[1, 2, 3, 4, 5].map((star) => (
                  <span key={star} onClick={() => handleRating(star)}>
                    {(userRating as number) >= star
                      ? getStar("full", "2rem")
                      : getStar("empty", "2rem")}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderOtherVersions = () => {
    return (
      <>
        {!store.cameraPreviewOn && (
          <div style={{ padding: "0 10px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: "10px",
              }}
            >
              <h3
                style={{ color: "gray", fontSize: "20px", fontStyle: "italic" }}
              >
                Other Versions
              </h3>
              {songDetailData && songDetailData?.versions.length > 1 ? (
                <ViewIcon
                  onClick={() => {
                    navigate("/songVersions", {
                      state: { songId: songDetailData.details.id },
                    });
                  }}
                />
              ) : (
                <></>
              )}
            </div>

            <Swiper
              slidesPerView={"auto"}
              spaceBetween={10}
              freeMode={true}
              modules={[FreeMode]}
              className="mySwiper"
              style={{
                marginLeft: "5px",
                marginTop: "10px",
                marginBottom: "20px",
              }}
            >
              {songDetailData?.versions?.map(
                (song, index) =>
                  song.active !== 1 && (
                    <SwiperSlide
                      style={{ width: "147px" }}
                      key={index}
                      onClick={() => {
                        setLoading(true);
                        getDetail(song.chord_id);
                      }}
                    >
                      <img
                        src={songDetailData.details.image_url}
                        alt={`Song ${songDetailData.details.name}`}
                        style={{
                          width: "147px",
                          height: "147px",
                          borderRadius: "20px",
                        }}
                      />
                      <h2
                        className="custom-font"
                        style={{
                          color: lightThemeOn ? "black" : "white",
                          textAlign: "center",
                          fontSize: "18px",
                          marginBottom: "0",
                        }}
                      >
                        {songDetailData.details.name}
                      </h2>
                      <h2
                        className="custom-font"
                        style={{
                          color: lightThemeOn ? "black" : "white",
                          textAlign: "center",
                          fontSize: "15px",
                          margin: "0",
                          fontWeight: "normal",
                        }}
                      >
                        {song.user.name}
                      </h2>
                    </SwiperSlide>
                  )
              )}
            </Swiper>
          </div>
        )}
      </>
    );
  };

  const renderLyricsText = () => {
    return (
      <div
        ref={shareImgRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          fontFamily: "sans-serif",
          backgroundColor: downloading
            ? lightThemeOn
              ? "#E9E9E9"
              : "#1A1A1A"
            : "transparent",
        }}
      >
        {downloading && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "0px 17px 0 17px",
                paddingTop: "20px",
              }}
            >
              <img src={logo} width={"95px"} height={"49px"} />
              <img src={guitarQr} width={"100px"} height={"100px"} />
            </div>
            <div style={bodyHeaderStyle()}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "0 17px",
                  height: loading ? "30px" : "auto",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <h1
                    style={{
                      fontSize: "15px",
                      fontWeight: "bold",
                      margin: "0",
                      color: headerColor,
                      marginBottom: "5px",
                      fontFamily: "Arial, sans-serif",
                    }}
                  >
                    {songDetailData?.details?.name || ""}
                  </h1>
                  <h1
                    style={{
                      fontSize: "15px",
                      fontWeight: "bold",
                      margin: "0",
                      color: "gray",
                      fontFamily: "Arial, sans-serif",
                    }}
                  >
                    {songDetailData?.details?.artist?.name || ""}
                  </h1>
                </div>
              </div>
            </div>
          </>
        )}
        <div
          style={{
            textAlign: "center",
            whiteSpace: "wrap",
            paddingTop: "40px",
          }}
        >
          {lyrics?.map((line, lineIndex) => (
            <div
              key={lineIndex}
              style={{
                height: "auto",
                position: "relative",
                lineHeight: "50px",
              }}
            >
              {isTagLine(line) ? (
                <TagLine
                  line={line}
                  lineIndex={lineIndex}
                  chordPositions={chordPositions}
                  lyricsColor={lyricsColor}
                  chordColor={chordColor}
                  lyricsFontSize={lyricsFontsize}
                  chordFontsize={chordFontsize}
                  handleClickedChord={setClickedChord}
                  store={store}
                />
              ) : (
                customSplit(line).map((word, wordIndex) => (
                  <Word
                    key={wordIndex}
                    id={`word_${lineIndex}-${wordIndex}`}
                    word={word}
                    line={line}
                    chord={chordPositions[`word_${lineIndex}-${wordIndex}`]}
                    lyricsFontSize={lyricsFontsize}
                    chordFontsize={chordFontsize}
                    lyricsColor={lyricsColor}
                    chordColor={chordColor}
                    handleClickedChord={setClickedChord}
                  />
                ))
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLyricsImg = () => {
    return (
      <>
        {lyricsImgFullScreen ? (
          <div
            onClick={() => setLyricsImgFullScreen(false)}
            style={{
              position: "fixed",
              top: 0,
              width: store.mainLayoutWidth,
              height: "100vh",
              backgroundColor: "black",
              zIndex: 2000,
            }}
          >
            <QuickPinchZoom onUpdate={onZoomUpdate}>
              <img
                ref={lyricsImgRef}
                src={songDetailData?.details?.chord_img || ""}
                style={{ height: "100vh" }}
              />
            </QuickPinchZoom>
          </div>
        ) : (
          <div
            onClick={() => setLyricsImgFullScreen(true)}
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "10px",
            }}
          >
            <CustomImage
              ref={lyricsImgRef}
              src={songDetailData?.details.chord_img || ""}
              height={"60vh"}
              skeletonWidth={store.mainLayoutWidth + "px"}
              style={{ height: "60vh", objectFit: "contain" }}
            />
          </div>
        )}
      </>
    );
  };

  const renderBody = () => {
    // const { showPlaylist, playlistOn } = useStore();
    const handleProfileClick = () => {
      if (songDetailData?.details?.user?.user_id === getDecoded().userId) {
        navigate("/profile");
        return;
      }
      const obj = { userId: songDetailData?.details?.user?.user_id };
      navigate("/userProfile", { state: obj });
    };

    const handleCameraChange = () => {
      if (getPlatform() === "web") return showPlayStoreModal();
      console.log("store.cameraPreviewOn: ", store.cameraPreviewOn);

      updateStoreFunc("cameraPreviewOn", !store.cameraPreviewOn);
    };

    const bothImgAndTextExist =
      songDetailData?.details?.chord_pro && songDetailData?.details?.chord_img;

    // useEffect(() => {}, [store]);

    const iconStyleCondition = () => {
      if (store.cameraPreviewOn) {
        return {
          display: "flex",
          // position: "absolute",
          flexDirection: "row",
          gap: "15px",
          zIndex: 1000,
          top: "px",
          color: "white",
          justifyContent: "flex-end",
          width: "100%",
        } as React.CSSProperties;
      } else {
        return { display: "flex" } as React.CSSProperties;
      }
    };

    return (
      <div
        style={{
          backgroundColor: backgroundColor,
          marginTop: `${videoHeight - 55}px`,
        }}
      >
        <div>
          <div style={bodyHeaderStyle()}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "0 17px",
                height: loading ? "30px" : "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: store.cameraPreviewOn ? "column" : "row",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <div style={{}}>
                  <h1
                    style={{
                      fontSize: "15px",
                      fontWeight: "bold",
                      margin: "0",
                      color: headerColor,
                      marginBottom: "5px",
                      fontFamily: "Arial, sans-serif",
                    }}
                  >
                    {songDetailData?.details?.name || ""}
                  </h1>
                  <h1
                    style={{
                      fontSize: "15px",
                      fontWeight: "bold",
                      margin: "0",
                      color: "gray",
                      fontFamily: "Arial, sans-serif",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      const obj = {
                        selectedTag: "Artist",
                        headerData: songDetailData?.details?.artist,
                      };
                      navigate("/home/songListsBySpecificId", { state: obj });
                    }}
                  >
                    {songDetailData?.details?.artist?.name || ""}
                  </h1>
                </div>
                <div style={iconStyleCondition()}>
                  {!store.recording &&
                    !lyricsImageOn &&
                    !store.cameraPreviewOn && (
                      <ButtonBase
                        onClick={handleCameraChange}
                        sx={{ margin: "0 15px" }}
                      >
                        {store.cameraPreviewOn ? <CameraOn /> : <CameraOff />}
                      </ButtonBase>
                    )}

                  {bothImgAndTextExist && !store.cameraPreviewOn && (
                    <ButtonBase
                      onClick={handleLyricsImageChange}
                      // sx={{ margin: "0 10px" }}
                    >
                      {lyricsImageOn ? (
                        <LyricsImageOnIcon />
                      ) : (
                        <LyricsImageOffIcon />
                      )}
                    </ButtonBase>
                  )}

                  {!store.recording && !store.cameraPreviewOn && (
                    <ButtonBase
                      // onClick={handleClickSetting}
                      onClick={() =>
                        showPlaylist(
                          songDetailData?.details.id,
                          songDetailData?.details.chord_id,
                          false
                        )
                      }
                      sx={{ margin: lyricsImageOn ? "0 10px" : "0 0px" }}
                    >
                      <BookMapOn />
                    </ButtonBase>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div
            className="scrollable-y remove-scrollbar"
            id="lyrics-detail"
            style={{ height: window.innerHeight - headerHeight - videoHeight }}
          >
            {lyricsImageOn ? renderLyricsImg() : renderLyricsText()}
            {!store.cameraPreviewOn && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  margin: "0 15px 0 10px",
                  marginTop: "20px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <CustomImage
                    src={songDetailData?.details?.user?.avatar as string}
                    borderRadius="20px"
                    width={"37px"}
                    height={"37px"}
                    onClick={(e) => handleProfileClick()}
                  />
                  <h5
                    style={{
                      fontSize: "14px",
                      fontWeight: "normal",
                      margin: "0",
                      marginLeft: "5px",
                      color: lightThemeOn ? "black" : "white",
                    }}
                    onClick={(e) => handleProfileClick()}
                  >
                    {songDetailData?.details?.user?.name}
                  </h5>
                </div>
                {!lyricsImageOn && (
                  <ButtonBase
                    style={{ width: "50px", height: "50px" }}
                    onClick={() => setDownloading(true)}
                  >
                    <ShareIcon
                      css={css`
                        path:first-of-type {
                          stroke: ${lightThemeOn ? "black" : "white"};
                        }
                      `}
                    />
                  </ButtonBase>
                )}
              </div>
            )}
            {renderRating()}
            {!store.cameraPreviewOn && (
              <div style={{ margin: "0 10px" }}>
                {songDetailData?.details?.tags &&
                  songDetailData?.details?.tags?.map((tag, index) => (
                    <div
                      onClick={() => {
                        const obj = { tagToSearch: "#" + tag.tag_title };
                        navigate("/home/search", { state: obj });
                      }}
                      key={index}
                      style={{
                        display: "inline-block",
                        backgroundColor: "#333",
                        color: "white",
                        borderRadius: "20px",
                        padding: "5px 10px",
                        margin: "5px",
                        fontSize: "14px",
                        height: "20px",
                      }}
                    >
                      {tag.tag_title}
                    </div>
                  ))}
              </div>
            )}
            {renderOtherVersions()}
          </div>
        </div>
      </div>
    );
  };

  const handleShare = async () => {
    if (shareImgRef.current === null) {
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 100)); // use this for render spin first otherwise spin will be rendered after generating url

    const sharedImg =
      shareBase64Obj.current[
        `${currentTransposeLevel.current}-${currentFontSize.current}`
      ];
    let base64Content;
    if (sharedImg) {
      base64Content = sharedImg;
    } else {
      const dataUrl = await htmlToImage.toPng(shareImgRef.current);
      base64Content = dataUrl.split(",")[1];
      shareBase64Obj.current[
        `${currentTransposeLevel.current}-${currentFontSize.current}`
      ] = base64Content;
    }
    saveAndShareBase64(
      base64Content,
      songDetailData?.details?.name + ".png" || "image.png"
    );
  };

  async function saveAndShareBase64(base64Content: string, fileName: string) {
    try {
      const folder = "mmGuitar";
      const path = `${folder}/${fileName}`;

      const forWeb = async () => {
        // Convert Base64 to Blob
        const byteCharacters = atob(base64Content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "image/png" });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        setDownloading(false);

        const file = new File([blob], fileName, { type: "image/png" });

        await navigator.share({
          files: [file],
        });
      };

      const forNative = async () => {
        const pass = await checkStoragePermission();
        if (!pass) {
          setDownloading(false);
          return;
        }

        const writeResult = await Filesystem.writeFile({
          path,
          data: base64Content,
          directory: Directory.Documents,
          recursive: true,
        });

        setDownloading(false);

        Modal.confirm({
          title: text["Share?"],
          content:
            text[
              "Lyrics Text has been saved in the Documents/mmGuitar directory. Would you like to share it now?"
            ],
          okText: text["Share"],
          cancelText: text["No"],
          centered: true,
          async onOk() {
            await Share.share({
              url: writeResult.uri,
            });
          },
        });
      };

      getPlatform() === "web" ? forWeb() : forNative();
    } catch (error) {
      console.error("Error saving or sharing the file:", error);
    }
  }

  const checkStoragePermission = async () => {
    const { publicStorage } = await Filesystem.checkPermissions();

    if (publicStorage === "denied") {
      goToSetting("storage");
      return false;
    }

    if (publicStorage !== "granted") {
      let requestedStatus = await Filesystem.requestPermissions();
      if (requestedStatus?.publicStorage !== "granted") return false;
    }
    return true;
  };

  const goToSetting = (v: string) => {
    let content: string = "";
    if (v === "storage") {
      content =
        text[
          "Please allow the Storage Permission in the app settings to proceed"
        ];
    }

    if (v === "camera") {
      content =
        text[
          "Please allow the Camera Permission in the app settings to proceed"
        ];
    }

    if (v === "audio") {
      content =
        text[
          "Please allow the Microphone Permission in the app settings to proceed"
        ];
    }

    Modal.confirm({
      title: text["Go To Setting"],
      content: content,
      okText: "Setting",
      cancelText: "Cancel",
      centered: true,
      onOk() {
        if (v === "storage" || v === "camera" || v === "audio") {
          NativeSettings.openAndroid({
            option: AndroidSettings.ApplicationDetails,
          });
        }
      },
    });
  };

  useEffect(() => {
    if (downloading) {
      handleShare();
    }
  }, [downloading]);

  const scrollIntervalId = useRef<NodeJS.Timeout | null>(null);

  const handleScroll = async () => {
    const lyricsContainer = document.getElementById("lyrics-detail");

    clearInterval(scrollIntervalId.current!);

    const slowestStep = await getSlowestScrollStep();

    const scrollSpeeds: Record<string, { interval: number; step: number }> = {
      "1x": { interval: slowestStep.interval, step: slowestStep.step },
      "2x": { interval: 40, step: 0.8 },
      "3x": { interval: 40, step: 1.2 },
      "4x": { interval: 40, step: 1.4 },
      "5x": { interval: 40, step: 1.6 },
    };

    if (scroll !== "0x" && lyricsContainer) {
      const hitBottom =
        lyricsContainer.scrollTop + lyricsContainer.clientHeight >=
        lyricsContainer.scrollHeight;
      const { interval, step } = scrollSpeeds[scroll] || {
        interval: 40,
        step: 0.4,
      };

      scrollIntervalId.current = setInterval(() => {
        if (!isUserScrolling.current && !hitBottom) {
          lyricsContainer.scrollBy({ top: step, behavior: "smooth" });
        }
      }, interval);
    }
  };

  useEffect(() => {
    handleScroll();

    return () => {
      if (scrollIntervalId.current) {
        clearInterval(scrollIntervalId.current);
      }
    };
  }, [scroll]);

  const init = async () => {
    awake();
    // await PrivacyScreen.enable();
    // await ads.showInterstitial();
  };

  const cleanUpFunc = () => {
    sleep();
    // PrivacyScreen.disable();

    if (window.location.pathname !== "/home/songListsBySpecificId") {
      // clean up for songListsBySpecificId
      updateStoreFunc("songListsBySpecificId", "data", null);
      updateStoreFunc("songListsBySpecificId", "hasMore", true);
      updateStoreFunc("songListsBySpecificId", "page", 1);
      updateStoreFunc("songListsBySpecificId", "searchQuery", "");
    }
  };

  useEffect(() => {
    if (store.cameraPreviewOn) {
      // PrivacyScreen.disable();
    } else {
      // PrivacyScreen.enable();
    }
  }, [store.cameraPreviewOn]);

  useEffect(() => {
    if (lyricsImageOn) {
      setScroll("0x");
    }
  }, [lyricsImageOn]);

  useEffect(() => {
    setIsChordImageDrawerOpen(true);
  }, [clickedChord]);

  useEffect(() => {
    if (!isChordImageDrawerOpen) setClickedChord("");
  }, [isChordImageDrawerOpen]);

  useEffect(() => {
    init();
    getDetail();
    return () => {
      cleanUpFunc();
    };
  }, []);

  const swipedLeftThreshold = -100;
  const swipedRightThreshold = 100;

  const handlers = useSwipeable({
    onSwiping: (eventData) => {
      // console.log("x swiping: ", eventData.deltaX)
      if (store.cameraPreviewOn || store.recording) return;
      setIsSwiping(true);
      swipedX.set(eventData.deltaX, true);
      // const c = "#2B6BAA";
      const c = "black";
      if (direction === "left" && eventData.deltaX < swipedLeftThreshold) {
        setSwipedColor(c);
      } else if (
        direction === "right" &&
        eventData.deltaX > swipedRightThreshold
      ) {
        setSwipedColor(c);
      } else {
        setSwipedColor("gray");
      }
    },
    onSwipeStart: (eventData) => {
      if (store.cameraPreviewOn || store.recording) return;

      setDirection(eventData.dir.toLowerCase() as any);
    },
    onSwipedLeft: () => {
      if (store.cameraPreviewOn || store.recording) return;

      const getX = swipedX.getPrevious() || 0;
      if (getX < swipedLeftThreshold) {
        // console.log("left")
        if (!rightSong) return;
        getDetail(rightSong?.chordId, rightSong?.songId);
        navigate("/songDetail", {
          state: {
            ...location.state,
            song_id: rightSong?.songId,
            chord_id: rightSong?.chordId,
          },
          replace: true,
        });
      }
      resetSwipe();
    },
    onSwipedRight: () => {
      if (store.cameraPreviewOn || store.recording) return;

      const getX = swipedX.getPrevious() || 0;
      if (getX > swipedRightThreshold) {
        // console.log("right")
        if (!leftSong) return;
        getDetail(leftSong?.chordId, leftSong?.songId);
        navigate("/songDetail", {
          state: {
            ...location.state,
            song_id: leftSong?.songId,
            chord_id: leftSong?.chordId,
          },
          replace: true,
        });
      }
      resetSwipe();
    },
    onSwiped: () => resetSwipe(),

    trackMouse: true,
  });

  const resetSwipe = () => {
    if (store.cameraPreviewOn || store.recording) return;
    setIsSwiping(false);
    swipedX.set(0); // Reset position smoothly
  };

  const leftOpacity = useTransform(swipedX, [-100, 0], [1, 0]); // Left song appears as we swipe right
  const rightOpacity = useTransform(swipedX, [0, 100], [0, 1]); // Right song appears as we swipe left
  const translateX = useTransform(swipedX, [-100, 100], [-1, 1]); // Smooth slide effect

  useEffect(() => {
    updateStoreFunc("showScrollUi", true);
  }, []);

  return (
    <div {...handlers} style={{ position: "relative" }}>
      {direction === "left" && rightSong && rightSong.name && (
        <motion.div
          style={{
            opacity: leftOpacity,
            x: translateX,
            position: "absolute",
            right: 10,
            color: "white",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              color: swipedColor,
              backgroundColor: "rgba(128, 128, 128, 0.5)",
              height: "100vh",
              borderRadius: "100% 0 0 100%",
              width: "120px",
              justifyContent: "center",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {/* <ArrowLeft css={css`path:first-of-type { stroke: ${swipedColor}}`} style={{marginRight: "5px"}}/> */}
            {rightSong.name} hello khl
          </div>
        </motion.div>
      )}
      {direction === "right" && leftSong && leftSong.name && (
        <motion.div
          style={{
            opacity: rightOpacity,
            x: translateX,
            position: "absolute",
            color: "white",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              color: swipedColor,
              backgroundColor: "rgba(128, 128, 128, 0.5)",
              height: "100vh",
              borderRadius: "0 100% 100% 0",
              width: "120px",
              justifyContent: "center",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {leftSong?.name} hello khl
            {/* <ArrowLeft  css={css`path:first-of-type { stroke: ${swipedColor}}`} style={{marginLeft: "5px",transform: 'scaleX(-1)'}}/> */}
          </div>
        </motion.div>
      )}
      <VideoCapture lightTheme={lightThemeOn} />
      <ChordImageDrawer
        chord={clickedChord}
        isOpen={isChordImageDrawerOpen}
        onClose={() => setIsChordImageDrawerOpen(false)}
      />
      <Spin spinning={downloading} fullscreen />
      <SettingDrawer
        getDetail={getDetail}
        isOpen={isSettingDrawerOpen}
        onClose={() => setIsSettingDrawerOpen(false)}
        handleFontSizeChange={handleFontSizeChange}
        handleLevelChange={handleChordLevelChange}
        lightThemeOn={lightThemeOn}
        handleThemeChange={handleThemeChange}
        lyricsImageOn={lyricsImageOn}
        isUserOwnChord={isUserOwnChord}
        handleClickEdit={handleClickEdit}
        songDetailData={songDetailData as SongDetailsType}
      />

      <Header
        songDetailData={songDetailData}
        scroll={scroll}
        handleClickSetting={handleClickSetting}
        handleClickScroll={handleClickScroll}
        handleLevelChange={handleChordLevelChange}
        isSettingDrawerOpen={isSettingDrawerOpen}
        lightThemeOn={lightThemeOn}
        lyricsImageOn={lyricsImageOn}
        handleLyricsImageChange={handleLyricsImageChange}
        loading={loading}
      />
      {loading ? (
        <Skeleton
          width={"100%"}
          height={"100%"}
          count={20}
          style={{ marginBottom: "10px", paddingTop: "15px" }}
        />
      ) : (
        renderBody()
      )}
      {store.screenType !== "mobile" && <Footer />}
      {/* {store.showScrollUi && <FloatingScrollUi />} */}
    </div>
  );
};

export default SongDetail;

type HeaderProps = {
  songDetailData: SongDetailsType | undefined;
  scroll: string;
  handleClickSetting: MouseEventHandler<HTMLButtonElement>;
  handleClickScroll: (value: number) => void;
  handleLevelChange: (param: { id: string; value: string }) => void;
  isSettingDrawerOpen: boolean;
  lightThemeOn: boolean;
  lyricsImageOn: boolean;
  handleLyricsImageChange: () => void;
  loading: boolean;
};

const Header: React.FC<HeaderProps> = ({
  songDetailData,
  scroll = "1x",
  handleClickSetting,
  handleClickScroll,
  handleLevelChange,
  isSettingDrawerOpen,
  lightThemeOn,
  lyricsImageOn,
  handleLyricsImageChange,
  loading,
}: HeaderProps) => {
  const [selectedLevel, setSelectedLevel] = useState({ id: "7", value: "0" });
  const [level, setLevel] = useState([
    { id: "0", value: "+7" },
    { id: "1", value: "+6" },
    { id: "2", value: "+5" },
    { id: "3", value: "+4" },
    { id: "4", value: "+3" },
    { id: "5", value: "+2" },
    { id: "6", value: "+1" },
    { id: "7", value: "0" },
    { id: "8", value: "-1" },
    { id: "9", value: "-2" },
    { id: "10", value: "-3" },
    { id: "11", value: "-4" },
    { id: "12", value: "-5" },
    { id: "13", value: "-6" },
    { id: "14", value: "-7" },
  ]);
  const { store, updateStoreFunc, showPlaylist } = useStore();

  const { previousLocation } = useRouteTracker();

  const navigatedFromEditLyrics = previousLocation === "/dataEntry/editLyrics";

  const dynamicBackHeader = navigatedFromEditLyrics ? (
    <BackHeader arrowColor={lightThemeOn ? "black" : "white"} to="/library" />
  ) : (
    <BackHeader arrowColor={lightThemeOn ? "black" : "white"} />
  );

  // const { showPlaylist, playlistOn } = useStore();

  // const handleCameraChange = () => {
  //   if (getPlatform() === "web") return showPlayStoreModal();
  //   updateStoreFunc("cameraPreviewOn", !store.cameraPreviewOn);
  // };
  const text = store.text;
  const bothImgAndTextExist =
    songDetailData?.details?.chord_pro && songDetailData?.details?.chord_img;

  const scrollUi = () => {
    const handleScrollMinus = () => {
      // extract number part
      const num = parseInt(scroll.replace("x", ""), 10);
      if (num > 1) {
        const newValue = num - 1;
        handleClickScroll(newValue);
      }
    };

    const handleScrollPlus = () => {
      const num = parseInt(scroll.replace("x", ""), 10);
      if (num < 5) {
        const newValue = num + 1;
        handleClickScroll(newValue);
      }
    };

    const handleClickScrollRefresh = () => {
      // handleClickScroll(0);
      console.log("hello scroll", scroll);
      scroll === "0x" ? handleClickScroll(1) : handleClickScroll(0);
    };

    const handleSrollTranspose = (action: "plus" | "minus" | "refresh") => {
      // const currentTranspose = selectedLevel.value;
      // console.log("call handleSrollTranspose");

      // console.log("selectedLevel", selectedLevel);
      let currentIndex = level.findIndex(
        (l) => l.value === selectedLevel.value
      );

      if (action === "minus" && currentIndex < level.length - 1) {
        setSelectedLevel(level[currentIndex + 1]);
        handleLevelChange(level[currentIndex + 1]);
      }
      if (action === "plus" && currentIndex > 0) {
        setSelectedLevel(level[currentIndex - 1]);
        handleLevelChange(level[currentIndex - 1]);
      }
      if (action === "refresh") {
        console.log("reset to 0");
        setSelectedLevel(level[7]); // resets to 0
        handleLevelChange(level[7]);
      }
    };

    return (
      <>
        {!store.recording && (
          <div
            style={{
              zIndex: 1000,
              display: "flex",
              flexDirection: "row",
              position: "absolute",
              bottom: 20,
              left: "50%",
              transform: "translate(-50%, 0)",
              width: "80%",
              // width:
              //   store.screenType === "mobile"
              //     ? window.innerWidth
              //     : config.mobileScreenWidth,

              color: "white",
              height: "85px",
              backgroundColor: "#1e1e1e",
              borderRadius: "20px",
              alignItems: "center", // centers vertically
              justifyContent: "center", // spreads children apart
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
              cursor: "pointer",
              gap: "20px",
              // marginBottom: "30px",
              padding: "0 20px", // optional: adds breathing room
              // margin: "0 5px 0 20px",
            }}
          >
            {/* Transpose */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                // border: "1px solid white",
              }}
            >
              <p
                style={{
                  fontSize: 15,
                  fontWeight: "bold",
                  margin: "0px 0px 5px",
                  color: "white",
                  fontFamily: "Arial, sans-serif",
                }}
              >
                Transpose : {selectedLevel.value}
              </p>

              <PlusMinusButton
                handleMinus={() => {
                  handleSrollTranspose("minus");
                }}
                handlePlus={() => {
                  handleSrollTranspose("plus");
                }}
                handleClickScroll={() => {
                  handleSrollTranspose("refresh");
                }}
                scroll={scroll}
                icon="refresh"
              />
            </div>
            {/* Transpose */}
            {/* Speed */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,

                // border: "1px solid white",
              }}
            >
              <p
                style={{
                  fontSize: 15,
                  fontWeight: "bold",
                  margin: "0px 0px 5px",
                  color: "white",
                  fontFamily: "Arial, sans-serif",
                }}
              >
                Scroll Speed : {scroll}
              </p>

              <PlusMinusButton
                handleMinus={handleScrollMinus}
                handlePlus={handleScrollPlus}
                handleClickScroll={handleClickScrollRefresh}
                scroll={scroll}
                icon="play"
              />
            </div>
            {/* Speed */}
          </div>
        )}
        {store.recording && (
          <div
            style={{
              zIndex: 1000,
              display: "flex",
              flexDirection: "column", // vertical layout
              position: "absolute",
              bottom: 20,
              right: "20px", // move to the left side
              // remove transform since we’re not centering horizontally
              width: "auto",
              color: "white",
              height: "auto",
              backgroundColor: "transparent",
              // backgroundColor: "#1e1e1e",
              // backgroundColor: "rgba(30, 30, 30, 0.5)", // semi-transparent
              border: "1px solid rgba(255, 255, 255, 0.3)", // subtle white border
              borderRadius: "20px",
              alignItems: "center", // centers buttons horizontally
              justifyContent: "center",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
              cursor: "pointer",
              gap: "10px",
              // padding: "0px 3px", // vertical padding for nice spacing
              // backdropFilter: "blur(6px)",
            }}
          >
            <PlusMinusButton
              handleMinus={handleScrollMinus}
              handlePlus={handleScrollPlus}
              handleClickScroll={handleClickScrollRefresh}
              scroll={scroll}
              icon="play"
              orientation="vertical"
            />
          </div>
        )}
      </>
    );
  };

  const handleCameraChangeHeader = () => {
    if (getPlatform() === "web") return showPlayStoreModal();
    console.log("store.cameraPreviewOn: ", store.cameraPreviewOn);

    updateStoreFunc("cameraPreviewOn", !store.cameraPreviewOn);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: store.cameraPreviewOn
          ? "transparent"
          : config.color.htmlColor,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        {store.recording ? (
          <div style={{ height: "55px" }}></div>
        ) : (
          dynamicBackHeader
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            marginRight: "10px",
          }}
        >
          {/* {bothImgAndTextExist && !store.cameraPreviewOn && (
            <ButtonBase onClick={handleLyricsImageChange}>
              {lyricsImageOn ? <LyricsImageOnIcon /> : <LyricsImageOnIcon />}
            </ButtonBase>
          )} */}
          {!store.cameraPreviewOn && (
            <ButtonBase onClick={handleClickSetting}>
              {lyricsImageOn ? <SettingOn /> : <SettingOn />}
            </ButtonBase>
          )}

          {store.cameraPreviewOn && !store.recording && (
            <ButtonBase
              onClick={() => {
                handleCameraChangeHeader();
              }}
            >
              {store.cameraPreviewOn ? <CameraOn /> : <CameraOff />}
            </ButtonBase>
          )}

          {store.cameraPreviewOn && !store.recording && (
            <ButtonBase
              onClick={() =>
                showPlaylist(
                  songDetailData?.details.id,
                  songDetailData?.details.chord_id,
                  false
                )
              }
              sx={{ margin: "0 10px" }}
            >
              <BookMapOn />
            </ButtonBase>
          )}
          {/* {!store.recording && (
            <ButtonBase onClick={handleClickSetting}>
              {isSettingDrawerOpen ? <SettingOn /> : <SettingOff />}
            </ButtonBase>
          )} */}
          {/* {!lyricsImageOn && <CustomButton onClick={handleClickScroll} active={false} style={{padding: '5px 15px',backgroundColor: "transparent",border: "1px solid gray",borderRadius: "15px", color: lightThemeOn || store.cameraPreviewOn ? "black": 'white'}}>
                  {scroll}
                </CustomButton>} */}
          {!lyricsImageOn && !loading && scrollUi()}
          {/* {!lyricsImageOn && !loading && FloatingScrollUi()} */}
          {/* {!store.recording && (
            <ButtonBase
              onClick={() =>
                showPlaylist(
                  songDetailData?.details.id,
                  songDetailData?.details.chord_id,
                  false
                )
              }
            >
              {playlistOn ? (
                <PlaylistOnIcon style={{ width: "30px", height: "30px" }} />
              ) : (
                <PlaylistOffIcon style={{ width: "30px", height: "30px" }} />
              )}
            </ButtonBase>
          )} */}
        </div>
      </div>
    </div>
  );
};

interface SettingDrawerProps {
  getDetail: () => void;
  isOpen: boolean;
  onClose: () => void;
  handleFontSizeChange: (param: { id: string; value: string }) => void;
  handleLevelChange: (param: { id: string; value: string }) => void;
  lightThemeOn: boolean;
  handleThemeChange: () => void;
  lyricsImageOn: boolean;
  isUserOwnChord: boolean;
  handleClickEdit: () => void;
  songDetailData: SongDetailsType;
}
const SettingDrawer: React.FC<SettingDrawerProps> = ({
  isOpen,
  onClose,
  handleFontSizeChange,
  handleLevelChange,
  lightThemeOn,
  handleThemeChange,
  lyricsImageOn,
  isUserOwnChord,
  handleClickEdit,
  songDetailData,
  getDetail,
}) => {
  const { store } = useStore();
  const text = store.text;

  const [fontSize, setFontSize] = useState([
    { id: "-4", value: "-4" },
    { id: "-3", value: "-3" },
    { id: "-2", value: "-2" },
    { id: "-1", value: "-1" },
    { id: "0", value: "0" },
    { id: "1", value: "1" },
    { id: "2", value: "2" },
    { id: "3", value: "3" },
    { id: "4", value: "4" },
  ]);

  const [selectedLevel, setSelectedLevel] = useState({ id: "7", value: "0" });
  const [selectedFontsize, setSelectedFontsize] = useState({
    id: "0",
    value: "0",
  });

  // useEffect(() => {
  //   console.log("1685 : selectedFontsize", selectedFontsize);
  // }, [selectedFontsize]);
  const [reportOn, setReportOn] = useState(false);
  const [selectedReport, setSelectedReport] = useState<null | number>(null);
  const [disapperFontSize, setDisapperFontSize] = useState(false);

  const reports = ["Wrong chords", "Wrong lyrics", "Sexual exploit", "Other"];

  const [level, setLevel] = useState([
    { id: "0", value: "+7" },
    { id: "1", value: "+6" },
    { id: "2", value: "+5" },
    { id: "3", value: "+4" },
    { id: "4", value: "+3" },
    { id: "5", value: "+2" },
    { id: "6", value: "+1" },
    { id: "7", value: "0" },
    { id: "8", value: "-1" },
    { id: "9", value: "-2" },
    { id: "10", value: "-3" },
    { id: "11", value: "-4" },
    { id: "12", value: "-5" },
    { id: "13", value: "-6" },
    { id: "14", value: "-7" },
  ]);

  const renderFontAndTransposeWheel = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-evenly",
          marginBottom: "30px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            borderColor: "#242424",
            borderStyle: "solid",
            borderWidth: "2px",
            borderRadius: "20px",
          }}
        >
          <h3 style={{ fontSize: "15px", color: "#606060", marginBottom: 0 }}>
            {text.Transpose}
          </h3>
          <div data-vaul-no-drag>
            <CustomWheelPicker
              uniqueId="wheel-songDetail-level"
              data={level}
              handleDataChange={(v) => {
                console.log("v", v);
                setSelectedLevel(v);
              }}
              selectedId={selectedLevel.id}
              height={150}
            />
          </div>
          <CustomButton
            style={{
              margin: "0 20px 20px 20px",
              paddingLeft: "40px",
              paddingRight: "40px",
            }}
            active
            onClick={() => handleLevelChange(selectedLevel)}
          >
            {text.Edit}
          </CustomButton>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            borderColor: "#242424",
            borderStyle: "solid",
            borderWidth: "2px",
            borderRadius: "20px",
          }}
        >
          <h3 style={{ fontSize: "15px", color: "#606060", marginBottom: 0 }}>
            {text["Change Font"]}
          </h3>
          <div data-vaul-no-drag>
            <CustomWheelPicker
              uniqueId="wheel-songDetail-fontsize"
              data={fontSize}
              handleDataChange={(v) => setSelectedFontsize(v)}
              selectedId={selectedFontsize.id}
              height={150}
            />
          </div>
          <CustomButton
            style={{
              margin: "0 20px 20px 20px",
              paddingLeft: "40px",
              paddingRight: "40px",
            }}
            active
            onClick={() => handleFontSizeChange(selectedFontsize)}
          >
            {text.Edit}
          </CustomButton>
        </div>
      </div>
    );
  };

  const renderFontSize = () => {
    return (
      <>
        <ButtonBase
          onClick={() => {
            console.log("clicked");
          }}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 10px",
            margin: "0px 30px 20px 30px",
            borderWidth: "0 0 2px 0",
            borderColor: "#242424",
            borderStyle: "solid",
          }}
        >
          <h3 style={{ fontSize: "15px" }}>
            {/* {isUserOwnChord ? text["Edit Song Lyrics"] : text["font"]} */}
            {text["FontSize"]} {selectedFontsize.value}
          </h3>
          {/* {isUserOwnChord ? <Edit /> : <Copy />} */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              // gap: "10px",
            }}
          >
            <Button
              onClick={() => {
                const currentIndex = fontSize.findIndex(
                  (item) => item.id === selectedFontsize.id
                );

                // Move up (decrease index) but stay in bounds
                const newIndex = Math.max(currentIndex - 1, 0);

                const newFontsize = fontSize[newIndex];

                setSelectedFontsize(newFontsize);
                handleFontSizeChange(newFontsize);
              }}
              sx={{
                width: 40, // set fixed width
                minWidth: 40, // avoid MUI default minWidth
                height: 40, // optional height
                padding: 0, // optional
              }}
            >
              <FontMinus />
            </Button>{" "}
            <Button
              onClick={() => {
                setSelectedFontsize({
                  id: "0",
                  value: "0",
                });
                handleFontSizeChange({
                  id: "0",
                  value: "0",
                });
              }}
              sx={{
                width: 40, // set fixed width
                minWidth: 40, // avoid MUI default minWidth
                height: 40, // optional height
                padding: 0, // optional
              }}
            >
              <FontRefresh />
            </Button>
            <Button
              onClick={() => {
                // Go to the next larger value

                const currentIndex = fontSize.findIndex(
                  (item) => item.id === selectedFontsize.id
                );

                // Move down (increase index) but stay in bounds
                const newIndex = Math.min(
                  currentIndex + 1,
                  fontSize.length - 1
                );

                const newFontsize = fontSize[newIndex]; // use fontSize array!

                setSelectedFontsize(newFontsize);
                handleFontSizeChange(newFontsize);
              }}
              sx={{
                width: 40, // set fixed width
                minWidth: 40, // avoid MUI default minWidth
                height: 40, // optional height
                padding: 0, // optional
              }}
            >
              <FontPlus />
            </Button>
          </div>
        </ButtonBase>
      </>
      // </div>
    );
  };

  const renderHeader = () => {
    const bothImgAndTextExist =
      songDetailData?.details?.chord_pro && songDetailData?.details?.chord_img;
    const onlyTextExist =
      songDetailData?.details?.chord_pro && !songDetailData?.details?.chord_img;
    const imgAlreadyExist = songDetailData?.details?.chord_img;
    const onlyLyricsImgAvailable =
      songDetailData?.details.chord_img && !songDetailData?.details.chord_pro;

    const lyricsTextExist = songDetailData?.details?.chord_pro;

    let uploadedLyricsImg = "";

    const handleUploadLyricsImage = async () => {
      if (!uploadedLyricsImg)
        return message.info("Please upload lyrics image first!");
      const params = {
        songId: songDetailData.details.id,
        chordId: songDetailData.details.chord_id,
        chordPro: songDetailData.details.chord_pro,
        name: songDetailData.details.name,
        artistId: songDetailData.details.artist.artist_id,
        chordImg: uploadedLyricsImg,
      };
      const result: any = await api.put(endpoints.song.songEdit, params);
      if (result.code === "200") {
        getDetail();
        message.success(result.message);
      } else {
        message.error(result.message);
      }
    };

    // Add Lyrics
    return (
      <>
        {(lyricsTextExist || (onlyLyricsImgAvailable && isUserOwnChord)) && (
          <ButtonBase
            onClick={handleClickEdit}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0 10px",
              margin: "20px 30px 20px 30px",
              borderWidth: "0 0 2px 0",
              borderColor: "#242424",
              borderStyle: "solid",
            }}
          >
            <h3 style={{ fontSize: "15px" }}>
              {onlyLyricsImgAvailable && isUserOwnChord
                ? text["Add Lyrics"]
                : isUserOwnChord
                ? text["Edit Song Lyrics"]
                : text["Copy"]}
            </h3>
            {isUserOwnChord ? <Edit /> : <Copy />}
          </ButtonBase>
        )}
        {/* {bothImgAndTextExist  && <ButtonBase onClick={handleLyricsImageChange}  style={{display: 'flex',justifyContent: "space-between", alignItems: "center",padding: '0 10px',margin: "0px 30px 20px 30px",borderWidth: "0 0 2px 0", borderColor: "#242424",borderStyle: 'solid'}}>
          <h3 style={{fontSize: "15px"}}>{text['Lyrics Image Available']}</h3>
          {lyricsImageOn ?  <EyeOnIcon/> : <EyeOffIcon/> }
        </ButtonBase>} */}
        {!lyricsImageOn && (
          <ButtonBase
            onClick={handleThemeChange}
            style={{
              display: "flex",
              padding: "0 10px",
              justifyContent: "space-between",
              alignItems: "center",
              margin: "0 30px 20px 30px",
              borderWidth: "0 0 2px 0",
              borderColor: "#242424",
              borderStyle: "solid",
            }}
          >
            <h3 style={{ fontSize: "15px" }}>{text["Dark Mode"]}</h3>
            {lightThemeOn ? <LightMode /> : <DarkMode />}
          </ButtonBase>
        )}
        {isUserOwnChord && (
          <ButtonBase
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0 10px",
              margin: "0 30px 20px 30px",
              borderWidth: "0 0 2px 0",
              borderColor: "#242424",
              borderStyle: "solid",
            }}
          >
            <CustomPhotoUploadType2
              onChange={(v) => (uploadedLyricsImg = v)}
              useCropper={false}
              showPreview={true}
              displayText={false}
              outlineSideText={"Upload Lyrics Image"}
              alwaysDisplayUploadOutline={true}
              circle={false}
              style={{
                width: "100%",
                height: "60px",
                borderRadius: 0,
                margin: 0,
                backgroundColor: "transparent",
              }}
            />
            {/* <CustomPhotoUpload onChange={(v) => uploadedLyricsImg = v} useCropper={false}  showPreview={false} displayText={false} outlineSideText={imgAlreadyExist ? text['Edit Lyrics Image'] : text['Upload Lyrics Image']} alwaysDisplayUploadOutline={true} circle={false} style={{width: "100%",height: "60px",borderRadius: 0, margin: 0, backgroundColor: "transparent"}}/> */}
            <span
              onClick={handleUploadLyricsImage}
              style={{ color: "#3373EF", fontSize: "15px" }}
            >
              Save
            </span>
          </ButtonBase>
        )}
      </>
    );
  };

  const renderFooter = () => {
    return (
      <>
        <ButtonBase
          onClick={() => setReportOn(true)}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 10px",
            margin: "0 30px 20px 30px",
            borderWidth: "0 0 2px 0",
            borderColor: "#242424",
            borderStyle: "solid",
          }}
        >
          <h3 style={{ fontSize: "15px", color: "#FF0000" }}>
            {[text["Report"]]}
          </h3>
          <ReportOnIcon />
        </ButtonBase>
      </>
    );
  };

  const renderReportUI = () => {
    const handleReport = async () => {
      if (selectedReport === null) {
        return message.info("Please select report first!");
      }
      const params = {
        songId: songDetailData.details.id,
        chordId: songDetailData.details.chord_id,
        reason: reports[selectedReport],
      };
      const result: any = await api.post(endpoints.song.report, params);
      if (result.code === "200") {
        message.success("Report sent successfully!");
        setReportOn(false);
        setSelectedReport(null);
      } else {
        message.error(result.message);
      }
    };

    const handleClickBack = () => {
      setReportOn(false);
      setSelectedReport(null);
    };

    return (
      <>
        <BackArrowIcon
          onClick={handleClickBack}
          style={{ marginLeft: "30px", marginTop: "20px" }}
        />
        {reports.map((r, i) => {
          return (
            <ButtonBase
              onClick={() => setSelectedReport(i)}
              style={{
                display: "flex",
                justifyContent: "start",
                alignItems: "center",
                padding: "0 10px",
                margin: i === 0 ? "20px 30px 20px 30px" : "0px 30px 20px 30px",
                borderWidth: "2px",
                borderColor: "#333333",
                borderStyle: "solid",
              }}
            >
              {selectedReport === i ? <ReportOnIcon /> : <ReportOffIcon />}
              <h3
                style={{
                  fontSize: "15px",
                  marginLeft: "10px",
                  color: selectedReport === i ? "red" : "white",
                }}
              >
                {(text as any)[r]}
              </h3>
            </ButtonBase>
          );
        })}
        <ButtonBase
          onClick={handleReport}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 10px",
            margin: "0 30px 20px 30px",
            borderWidth: "0 0 2px 0",
            borderColor: "#242424",
            borderStyle: "solid",
          }}
        >
          <h3 style={{ fontSize: "15px", color: "#FF0000" }}>
            {[text["Report"]]}
          </h3>
          <ReportOnIcon />
        </ButtonBase>
      </>
    );
  };

  // useEffect(() => {
  //   let timer: any;

  //   if (store.recording) {
  //     setDisapperFontSize(true);

  //     timer = setTimeout(() => {
  //       setDisapperFontSize(false);
  //     }, 5000);
  //   }

  //   return () => clearTimeout(timer);
  // }, [store.recording]);

  // useEffect(() => {
  //   console.log("store.recording changed: ", store.recording);
  // }, [store.recording]);

  return (
    <div>
      <CustomDrawer isOpen={isOpen} onClose={onClose}>
        {reportOn ? (
          <>{renderReportUI()}</>
        ) : (
          <>
            {renderHeader()}
            {!lyricsImageOn && renderFontSize()}
            {/* {!lyricsImageOn && renderFontAndTransposeWheel()} */}
            {renderFooter()}
          </>
        )}
      </CustomDrawer>
    </div>
  );
};

const isTagLine = (line: string) => {
  return line.startsWith("(");
};

const getTitleInsideParentheses = (line: string) => {
  const match = line.match(/\(([^)]+)\)/);
  return match ? match[1] : "";
};

type TagLineProps = {
  line: string;
  lineIndex: number;
  chordPositions: Record<string, string>;
  lyricsColor: string;
  chordColor: string;
  lyricsFontSize: string;
  chordFontsize: string;
  handleClickedChord: (value: string) => void;
  store: any;
};
const TagLine: React.FC<TagLineProps> = ({
  line,
  lineIndex,
  chordPositions,
  lyricsColor,
  chordColor,
  lyricsFontSize,
  chordFontsize,
  handleClickedChord,
  store,
}) => (
  <div className="tag-line">
    {customSplit(line).map((word, wordIndex) =>
      word.startsWith("(") ? (
        <TitleComponent
          key={wordIndex}
          lineIndex={lineIndex}
          title={getTitleInsideParentheses(line)}
          store={store}
        />
      ) : (
        <Word
          key={`word_${lineIndex}-${wordIndex}`}
          id={`word_${lineIndex}-${wordIndex}`}
          word={word}
          line={line}
          chord={chordPositions[`word_${lineIndex}-${wordIndex}`]}
          lyricsFontSize={lyricsFontSize}
          chordFontsize={chordFontsize}
          tagLine={true}
          lyricsColor={lyricsColor}
          chordColor={chordColor}
          handleClickedChord={handleClickedChord}
        />
      )
    )}
  </div>
);

interface TitleComponentProps {
  title: string;
  lineIndex: number;
  store: any;
}

const TitleComponent: React.FC<TitleComponentProps> = ({
  title,
  lineIndex,
  store,
}) => {
  return (
    <div>
      <div
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "#3373EF",
          position: "absolute",
          left: "0px",
          bottom: "5px",
          padding: "0 8px",
        }}
      >
        <Tag style={{ marginRight: "5px" }} />
        <div
          style={{
            textAlign: "start",
            userSelect: "none",
            // position: "relative",
          }}
        >
          <div>{title}</div>
          {/* <div
            style={{
              // marginTop: "5px",
              top: "35px",
              position: "absolute",
              width:
                store.screenType === "mobile" ? "300px" : store.mainLayoutWidth,
              height: "1px",
              backgroundColor: "#414141",
            }}
          ></div> */}
        </div>
      </div>
    </div>
  );
};

type WordProps = {
  word: string;
  line: string;
  id: string;
  chord?: string;
  lyricsFontSize?: string;
  chordFontsize?: string;
  tagLine?: boolean;
  lyricsColor: string;
  chordColor: string;
  handleClickedChord: (value: string) => void;
};

const Word: React.FC<WordProps> = ({
  word,
  line,
  id,
  chord,
  lyricsFontSize = "16px",
  chordFontsize = "12px",
  tagLine = false,
  lyricsColor,
  chordColor,
  handleClickedChord,
}) => {
  const style = {
    display: "inline-block",
    margin: "0px",
    position: "relative",
    color: lyricsColor,
    borderLeft: "none",
    fontSize: lyricsFontSize,
    fontFamily: "Arial, sans-serif",
  } as React.CSSProperties;

  const emptySpace = tagLine ? "" : "5px";

  const offset = isTagLine(line)
    ? "5px"
    : isEnglishWord(word)
    ? "-15px"
    : "-15px";

  return (
    <span style={style}>
      <div
        id={`box-${id}`}
        className="chord-box"
        style={{
          position: "absolute",
          top: offset,
          left: "50%",
          transform: "translateX(-50%)",
          width: "10px",
          height: "10px",
        }}
      >
        {chord && (
          <Chord
            key={`${id}-${chord}`}
            chord={chord}
            containerId={id}
            chordFontSize={chordFontsize}
            chordColor={chordColor}
            handleClickedChord={handleClickedChord}
          />
        )}
      </div>
      {word === " " ? (
        <div
          css={css`
            font-family: "Arial, sans-serif" !important;
          `}
          style={{ width: emptySpace, color: "transparent" }}
        >
          င
        </div>
      ) : (
        word
      )}
    </span>
  );
};

type ChordProps = {
  chord: string;
  containerId: string;
  chordFontSize: string;
  chordColor: string;
  handleClickedChord: (value: string) => void;
};

const Chord: React.FC<ChordProps> = ({
  chord,
  containerId,
  chordFontSize,
  chordColor,
  handleClickedChord,
}) => {
  const style = {
    fontSize: chordFontSize,
    touchAction: "none",
    color: chordColor,
    fontWeight: "bold",
    cursor: "pointer",
    zIndex: 1000,
    position: "fixed",
    top: -8,
    right: 0,
    fontFamily: "sans-serif",
  } as React.CSSProperties;

  return (
    <div style={{ position: "relative" }}>
      <div
        style={style}
        className="chord"
        onClick={() => handleClickedChord(chord)}
      >
        {chord}
      </div>
    </div>
  );
};

interface ChordImageDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  chord: string;
}

const ChordImageDrawer: React.FC<ChordImageDrawerProps> = ({
  isOpen,
  onClose,
  chord,
}) => {
  const { store } = useStore();
  const imageUrl = getChordImages()[chord];

  if (!isOpen || !imageUrl) return <></>;

  let img = "";
  try {
    img = require("../../assets" + imageUrl[0]);
  } catch (error) {
    return <></>;
  }

  return (
    <div>
      <CustomDrawer
        isOpen={isOpen}
        onClose={onClose}
        outsideInteraction={false}
      >
        <CustomImage
          src={img}
          width={
            store.screenType === "mobile" ? "100%" : config.mobileScreenWidth
          }
          height={"420px"}
          borderRadius="50px 50px 0 0"
        />
        {/* <Swiper
              className="mySwiper"
              centeredSlides={true}
              autoplay={{
              delay: 2500,
              disableOnInteraction: false,
              }}
              pagination={{
                clickable: true,
              }}
              modules={[Autoplay, Pagination]}
          >     
            {
              imageUrl?.map((chordImage, index) => {
                let img = "";
                try {
                  img = require("../../assets" + chordImage);
                } catch (error) {
                  return <></>
                }
                return (
                  <SwiperSlide key={index} style={{width: "100%"}}>
                    <CustomImage  src={img} width={store.screenType === "mobile" ? window.innerWidth : config.mobileScreenWidth} height={"400px"} borderRadius="25px 25px 0 0"/>
                  </SwiperSlide>
                )
              })
            } 
            
          </Swiper> */}
      </CustomDrawer>
    </div>
  );
};
